"use client";

import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@repo/utils/utils";
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@repo/ui";
import { PopoverContainerContext } from "@repo/ui";

export interface SearchableSelectOption {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  listClassName?: string;
  popoverSideOffset?: number;
  disabled?: boolean;
  container?: Element | DocumentFragment | null;
  /**
   * Render the popover inline instead of portaling it to `document.body`.
   * Required when this lives inside a Radix `Dialog` — otherwise the popover
   * escapes the dialog's focus trap and cmdk's `onSelect` never fires.
   */
  noPortal?: boolean;
  defaultOpen?: boolean;
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onValueChange,
  placeholder = "Select an option...",
  searchPlaceholder = "Search...",
  emptyMessage = "No results found.",
  className,
  listClassName,
  popoverSideOffset,
  disabled = false,
  container,
  noPortal,
  defaultOpen = false,
}) => {
  const [open, setOpen] = React.useState(defaultOpen);
  // Inside a modal dialog, portal into the dialog node (from context) so the
  // popover stays interactive; an explicit `container`/`noPortal` still wins.
  const ctxContainer = React.useContext(PopoverContainerContext);

  const selectedOption = options.find((option) => option.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between font-normal field-focus focus-visible:ring-[0.5px] focus-visible:ring-input-focus",
            className,
          )}
          disabled={disabled}
        >
          <span className={cn(!selectedOption && "text-muted-foreground")}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        sideOffset={popoverSideOffset}
        container={container ?? ctxContainer}
        noPortal={noPortal}
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList className={listClassName}>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  onSelect={() => {
                    onValueChange(option.value);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === option.value ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
